import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { adminApiService, PlatformStats, PendingReview, RecentActivity } from '../services/adminApi';
import {
    Users,
    Building2,
    Briefcase,
    BarChart2,
    ArrowRight
} from 'lucide-react';

/**
 * Main Admin Dashboard Page
 * Route: /admin/dashboard
 */
const AdminDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState<PlatformStats | null>(null);
    const [pendingReviews, setPendingReviews] = useState<PendingReview[]>([]);
    const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
    const [loading, setLoading] = useState(true);
    const [reviewsLoading, setReviewsLoading] = useState(true);
    const [activityLoading, setActivityLoading] = useState(true);

    // Fetch platform stats
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const data = await adminApiService.getStats();
                setStats(data);
            } catch (error) {
                console.error('Failed to fetch stats:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    // Fetch pending reviews
    useEffect(() => {
        const fetchReviews = async () => {
            try {
                const data = await adminApiService.getPendingReviews();
                setPendingReviews(data.slice(0, 3)); // Show only first 3
            } catch (error) {
                console.error('Failed to fetch pending reviews:', error);
            } finally {
                setReviewsLoading(false);
            }
        };

        fetchReviews();
    }, []);

    // Fetch recent activity
    useEffect(() => {
        const fetchActivity = async () => {
            try {
                const data = await adminApiService.getRecentActivity();
                setRecentActivity(data);
            } catch (error) {
                console.error('Failed to fetch recent activity:', error);
            } finally {
                setActivityLoading(false);
            }
        };

        fetchActivity();
    }, []);

    // Calculate dynamic pie chart percentages
    const getPieChartGradient = () => {
        if (!stats || stats.totalUsers === 0) {
            return 'conic-gradient(#E5E7EB 0% 100%)'; // Gray if no data
        }

        const candidatesPct = (stats.candidates / stats.totalUsers) * 100;
        const companiesPct = (stats.companies / stats.totalUsers) * 100;
        const recruitersPct = (stats.recruiters / stats.totalUsers) * 100;
        const adminsPct = (stats.admins / stats.totalUsers) * 100;

        return `conic-gradient(
            #015FA6 0% ${candidatesPct}%,
            #6EABFF ${candidatesPct}% ${candidatesPct + companiesPct}%,
            #CFE3FF ${candidatesPct + companiesPct}% ${candidatesPct + companiesPct + recruitersPct}%,
            #FFE5CC ${candidatesPct + companiesPct + recruitersPct}% 100%
        )`;
    };

    // Format timestamp for display
    const formatTimestamp = (timestamp: string) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    };

    return (
        <AdminLayout isHomePage={true}>
            {/* Main Content Grid */}
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                {/* Left Column */}
                <div className="flex-1 space-y-4 lg:space-y-6 min-w-0">
                    {/* Platform Statistics */}
                    <div className="bg-white rounded-2xl p-4 lg:p-6 overflow-hidden">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 lg:mb-6">
                            <div>
                                <h3 className="text-base lg:text-lg font-semibold text-[#0B111E]">Platform statistics</h3>
                                <p className="text-xs font-normal text-[#757575]">Showing statistics for the last 7 days</p>
                            </div>
                            <div className="flex border border-[#CBCBCB] rounded-lg flex-shrink-0">
                                <button className="px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium text-white bg-[#015FA6] rounded-l-lg">Week</button>
                                <button className="px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium text-[#757575]">Month</button>
                                <button className="px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium text-[#757575] rounded-r-lg">Year</button>
                            </div>
                        </div>

                        {/* Chart Area - Empty State with minimal bars */}
                        <div className="h-[180px] sm:h-[240px] relative">
                            {/* No data overlay */}
                            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                                <div className="text-center bg-white/90 px-4 sm:px-6 py-3 sm:py-4 rounded-lg">
                                    <BarChart2 className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 text-[#CBCBCB]" />
                                    <p className="text-xs sm:text-sm font-medium text-[#757575]">No activity data yet</p>
                                    <p className="text-[10px] sm:text-xs text-[#A5A5A5]">Platform analytics will appear here</p>
                                </div>
                            </div>

                            {/* Minimal bars for design */}
                            <div className="h-full flex items-end gap-1 sm:gap-2 opacity-20">
                                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                                    <div key={index} className="flex-1 flex flex-col items-center justify-end h-full">
                                        <div className="w-full bg-gradient-to-t from-[#015FA6]/20 to-[#015FA6]/5 rounded-t-lg" style={{ height: '5%' }}></div>
                                        <span className="text-[10px] sm:text-xs text-[#A5A5A5] mt-1 sm:mt-2">{day}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Legend */}
                        <div className="flex flex-wrap gap-3 sm:gap-4 mt-3 sm:mt-4">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-[#015FA6] rounded"></div>
                                <span className="text-[10px] sm:text-xs text-[#757575]">New Users</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-[#6EABFF] rounded"></div>
                                <span className="text-[10px] sm:text-xs text-[#757575]">Active Sessions</span>
                            </div>
                        </div>
                    </div>

                    {/* Platform Overview - Mobile version */}
                    <div className="lg:hidden bg-white rounded-2xl p-4 overflow-hidden">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-base font-semibold text-[#0B111E]">Platform Overview</h3>
                            <span className="text-xs text-[#515151]">Current</span>
                        </div>
                        <p className="text-sm text-[#757575] mb-4">
                            Distribution of users across the platform.
                        </p>

                        {/* Mobile Pie Chart - Stacked layout */}
                        <div className="flex flex-col sm:flex-row items-center gap-4">
                            <div className="w-[140px] h-[140px] sm:w-[160px] sm:h-[160px] rounded-full relative flex items-center justify-center flex-shrink-0" style={{
                                background: getPieChartGradient()
                            }}>
                                <div className="w-[90px] h-[90px] sm:w-[100px] sm:h-[100px] rounded-full bg-white flex items-center justify-center flex-col">
                                    <span className="text-base sm:text-lg font-semibold text-[#0B111E]">
                                        {loading ? '...' : stats?.totalUsers || 0}
                                    </span>
                                    <span className="text-[10px] sm:text-xs text-[#515151]">Total Users</span>
                                </div>
                            </div>

                            {/* Legend - Grid on mobile */}
                            <div className="grid grid-cols-2 sm:flex sm:flex-col gap-2 w-full sm:w-auto sm:flex-1">
                                <div className="flex justify-between items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-[#015FA6] rounded-full flex-shrink-0"></div>
                                        <span className="text-xs font-medium text-[#515151]">Candidates</span>
                                    </div>
                                    <span className="text-xs font-medium text-[#0B111E]">
                                        {loading ? '...' : stats?.candidates || 0}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-[#6EABFF] rounded-full flex-shrink-0"></div>
                                        <span className="text-xs font-medium text-[#515151]">Companies</span>
                                    </div>
                                    <span className="text-xs font-medium text-[#0B111E]">
                                        {loading ? '...' : stats?.companies || 0}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-[#CFE3FF] rounded-full flex-shrink-0"></div>
                                        <span className="text-xs font-medium text-[#515151]">Recruiters</span>
                                    </div>
                                    <span className="text-xs font-medium text-[#0B111E]">
                                        {loading ? '...' : stats?.recruiters || 0}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-[#FFE5CC] rounded-full flex-shrink-0"></div>
                                        <span className="text-xs font-medium text-[#515151]">Admins</span>
                                    </div>
                                    <span className="text-xs font-medium text-[#0B111E]">
                                        {loading ? '...' : stats?.admins || 0}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Pending Reviews */}
                    <div className="bg-white rounded-2xl p-4 lg:p-6 overflow-hidden">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-base lg:text-lg font-semibold text-[#0B111E]">Pending Reviews</h3>
                            <button
                                onClick={() => navigate('/admin/review-candidates')}
                                className="text-xs sm:text-sm font-medium text-[#015FA6] flex items-center gap-1 hover:underline"
                            >
                                View all
                                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                        </div>

                        {/* Review List */}
                        <div className="space-y-2">
                            {reviewsLoading ? (
                                <div className="text-center py-8">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#015FA6]"></div>
                                </div>
                            ) : pendingReviews.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-sm text-[#757575]">No pending reviews</p>
                                </div>
                            ) : (
                                pendingReviews.map((item, index) => {
                                    const diffMs = new Date().getTime() - new Date(item.created_at).getTime();
                                    const diffDays = Math.floor(diffMs / 86400000);
                                    const isNew = diffDays <= 1;
                                    const isUrgent = diffDays >= 3;

                                    return (
                                        <div key={item._id} className={`p-2 rounded flex items-center justify-between gap-2 ${index === 0 ? 'bg-[#F9F9F9]' : 'bg-white'} border-b border-[#EDEDED]`}>
                                            <div className="flex items-center gap-2 min-w-0">
                                                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#E3F2FD] rounded-full flex items-center justify-center flex-shrink-0">
                                                    <span className="text-xs sm:text-sm font-semibold text-[#015FA6]">
                                                        {(item.name || item.email).charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div className="min-w-0">
                                                    <h4 className="text-xs sm:text-sm font-medium text-[#0B111E] truncate">{item.name || item.email}</h4>
                                                    <p className="text-[10px] sm:text-xs text-[#757575] truncate">
                                                        {item.type.charAt(0).toUpperCase() + item.type.slice(1)} • {diffDays}d ago
                                                    </p>
                                                </div>
                                            </div>
                                            {(isNew || isUrgent) && (
                                                <span className={`text-[10px] sm:text-xs px-2 py-1 rounded flex-shrink-0 ${isUrgent ? 'text-[#FF5210] bg-[#FFEEEE]' : 'text-[#015FA6] bg-[#E3F2FD]'}`}>
                                                    {isUrgent ? 'Urgent' : 'New'}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Quick Actions Card */}
                    <div className="bg-white rounded-2xl p-4 lg:p-6 overflow-hidden">
                        <h3 className="text-base lg:text-lg font-semibold text-[#0B111E] mb-4">Quick Actions</h3>
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                            <button
                                onClick={() => navigate('/admin/review-candidates')}
                                className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-[#015FA6] bg-[#F1F3F4] rounded-lg hover:bg-[#E3F2FD] transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
                            >
                                <Users className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
                                <span className="truncate">Review Candidates</span>
                            </button>
                            <button
                                onClick={() => navigate('/admin/review-companies')}
                                className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-[#015FA6] bg-[#F1F3F4] rounded-lg hover:bg-[#E3F2FD] transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
                            >
                                <Building2 className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
                                <span className="truncate">Review Companies</span>
                            </button>
                            <button
                                onClick={() => navigate('/admin/manage-jobs')}
                                className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-[#015FA6] bg-[#F1F3F4] rounded-lg hover:bg-[#E3F2FD] transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
                            >
                                <Briefcase className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
                                <span className="truncate">Manage Jobs</span>
                            </button>
                            <button
                                onClick={() => navigate('/admin/analytics')}
                                className="px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-[#015FA6] bg-[#F1F3F4] rounded-lg hover:bg-[#E3F2FD] transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
                            >
                                <BarChart2 className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
                                <span className="truncate">Analytics</span>
                            </button>
                        </div>
                    </div>

                    {/* Recent Activity - Mobile version */}
                    <div className="lg:hidden bg-white rounded-2xl p-4 overflow-hidden">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-base font-semibold text-[#0B111E]">Recent Activity</h3>
                        </div>

                        {/* Activity List */}
                        <div className="space-y-0 overflow-y-auto max-h-[250px]">
                            {activityLoading ? (
                                <div className="text-center py-8">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#015FA6]"></div>
                                </div>
                            ) : recentActivity.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-sm text-[#757575]">No recent activity</p>
                                </div>
                            ) : (
                                recentActivity.slice(0, 5).map((activity, index) => (
                                    <div key={activity._id} className={`p-2 border-b border-[#F9F9F9] ${index === 1 ? 'bg-[#F9F9F9]' : 'bg-white'}`}>
                                        <div className="flex items-start gap-2">
                                            <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${activity.type === 'candidate' ? 'bg-[#E3F2FD]' :
                                                activity.type === 'company' ? 'bg-[#FEF3C7]' :
                                                    activity.type === 'recruiter' ? 'bg-[#F3E8FF]' :
                                                        'bg-[#DBEAFE]'
                                                }`}>
                                                <span className={`text-xs font-semibold ${activity.type === 'candidate' ? 'text-[#015FA6]' :
                                                    activity.type === 'company' ? 'text-[#F59E0B]' :
                                                        activity.type === 'recruiter' ? 'text-[#9333EA]' :
                                                            'text-[#3B82F6]'
                                                    }`}>
                                                    {activity.user.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start gap-2">
                                                    <h4 className="text-xs font-medium text-[#0B111E] truncate">{activity.user}</h4>
                                                    <span className="text-[10px] text-[#757575] flex-shrink-0">{formatTimestamp(activity.timestamp)}</span>
                                                </div>
                                                <p className="text-[10px] text-[#515151] truncate">{activity.action}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column - Desktop only */}
                <div className="hidden lg:block w-full lg:w-[320px] xl:w-[400px] 2xl:w-[450px] space-y-6 min-w-0">
                    {/* Platform Overview */}
                    <div className="bg-white rounded-2xl p-6 h-[320px]">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold text-[#0B111E]">Platform Overview</h3>
                            <span className="text-xs text-[#515151]">Current</span>
                        </div>
                        <p className="text-base text-[#757575] mb-6">
                            Distribution of users across the platform.
                        </p>

                        {/* Pie Chart */}
                        <div className="flex items-center gap-6">
                            <div className="w-[185px] h-[185px] rounded-full relative flex items-center justify-center" style={{
                                background: getPieChartGradient()
                            }}>
                                <div className="w-[120px] h-[120px] rounded-full bg-white flex items-center justify-center flex-col">
                                    <span className="text-lg font-semibold text-[#0B111E]">
                                        {loading ? '...' : stats?.totalUsers || 0}
                                    </span>
                                    <span className="text-xs text-[#515151]">Total Users</span>
                                </div>
                            </div>

                            {/* Legend */}
                            <div className="flex-1 space-y-2">
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-3">
                                        <div className="w-4 h-4 bg-[#015FA6] rounded-full"></div>
                                        <span className="text-sm font-medium text-[#515151]">Candidates</span>
                                    </div>
                                    <span className="text-sm font-medium text-[#0B111E]">
                                        {loading ? '...' : stats?.candidates || 0}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-3">
                                        <div className="w-4 h-4 bg-[#6EABFF] rounded-full"></div>
                                        <span className="text-sm font-medium text-[#515151]">Companies</span>
                                    </div>
                                    <span className="text-sm font-medium text-[#0B111E]">
                                        {loading ? '...' : stats?.companies || 0}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-3">
                                        <div className="w-4 h-4 bg-[#CFE3FF] rounded-full"></div>
                                        <span className="text-sm font-medium text-[#515151]">Recruiters</span>
                                    </div>
                                    <span className="text-sm font-medium text-[#0B111E]">
                                        {loading ? '...' : stats?.recruiters || 0}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-3">
                                        <div className="w-4 h-4 bg-[#FFE5CC] rounded-full"></div>
                                        <span className="text-sm font-medium text-[#515151]">Admins</span>
                                    </div>
                                    <span className="text-sm font-medium text-[#0B111E]">
                                        {loading ? '...' : stats?.admins || 0}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="bg-white rounded-2xl p-4 md:p-6 overflow-hidden">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-base md:text-lg font-semibold text-[#0B111E]">Recent Activity</h3>
                        </div>

                        {/* Activity List */}
                        <div className="space-y-0 overflow-y-auto max-h-[300px]">
                            {activityLoading ? (
                                <div className="text-center py-12">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#015FA6]"></div>
                                </div>
                            ) : recentActivity.length === 0 ? (
                                <div className="text-center py-12">
                                    <p className="text-sm text-[#757575]">No recent activity</p>
                                </div>
                            ) : (
                                recentActivity.map((activity, index) => (
                                    <div key={activity._id} className={`p-2 border-b border-[#F9F9F9] ${index === 1 ? 'bg-[#F9F9F9]' : index === 4 ? 'bg-[#EDEDED]' : 'bg-white'}`}>
                                        <div className="flex items-start gap-2">
                                            <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${activity.type === 'candidate' ? 'bg-[#E3F2FD]' :
                                                activity.type === 'company' ? 'bg-[#FEF3C7]' :
                                                    activity.type === 'recruiter' ? 'bg-[#F3E8FF]' :
                                                        'bg-[#DBEAFE]'
                                                }`}>
                                                <span className={`text-sm font-semibold ${activity.type === 'candidate' ? 'text-[#015FA6]' :
                                                    activity.type === 'company' ? 'text-[#F59E0B]' :
                                                        activity.type === 'recruiter' ? 'text-[#9333EA]' :
                                                            'text-[#3B82F6]'
                                                    }`}>
                                                    {activity.user.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start">
                                                    <h4 className="text-sm font-medium text-[#0B111E] truncate">{activity.user}</h4>
                                                    <span className="text-[10px] text-[#757575] flex-shrink-0">{formatTimestamp(activity.timestamp)}</span>
                                                </div>
                                                <p className="text-xs text-[#515151] truncate">{activity.action}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminDashboard;
